import {
  ObservedProperty,
  Dict,
  DSElementSetupHooks,
  DSCustomElement,
  ConstructorWithAttributes,
} from '../core-types';
import { indexBy, toDashCase, toCamelCase, objectsAreEqual } from '../../utility/helpers';
import { BehaviorSubject } from 'rxjs';
import { Maybe } from 'maybe-not';

export function enableProps(
  props: ObservedProperty[]
): { fnHashByName: Dict<any>; hooks: DSElementSetupHooks } {
  const fnHashByName = indexBy('name', props);
  return {
    fnHashByName,
    hooks: {
      init(element: DSCustomElement): void {
        element.__innerProps$ = new BehaviorSubject({});
      },
      connectedCallback(element: DSCustomElement): void {
        const initialProps = element.__innerProps$!.getValue();
        let updatedProps: Dict<any> = { ...initialProps };
        element.constructor.observedAttributes!.forEach(attrName => {
          /* set all observed attrs as properties on connect
             as initial values do not trigger attribute changed callback
             attrs set properties which proxy property behavior subject */
          if (element.getAttribute(attrName) === null) {
            return;
          }

          setPropFromAttr(
            element,
            attrName,
            element.getAttribute(attrName),
            Maybe.just(fnHashByName),
            updatedProps
          );
        });

        if (!objectsAreEqual(initialProps, updatedProps)) {
            element.__innerProps$!.next(updatedProps);
        }
      },
      classSetup(
        ConstructF: (() => DSCustomElement & { observedAttributes?: string[] })
      ): void {
        Object.assign(ConstructF, {
          observedAttributes: props
            .filter(x => x.reflectAs)
            .map(x => x.name)
            .map(toDashCase),
        });

        props.forEach((prop: ObservedProperty) => {
          Object.defineProperty(ConstructF.prototype, prop.name, {
            get(this: DSCustomElement): any {
              return this.__innerProps$!.getValue()[prop.name];
            },
            set(this: DSCustomElement, val: any): void {
              if (this.__innerProps$!.getValue()[prop.name] === val) {
                return;
              }

              const dashedName = toDashCase(prop.name);

              const observed = (ConstructF as ConstructorWithAttributes).observedAttributes!.includes(
                dashedName);
              if (observed) {
                if (val === true) {
                  this.setAttribute(dashedName, '');
                } else if (val || val === 0 || val === '') {
                  this.setAttribute(dashedName, String(val));
                } else {
                  this.removeAttribute(dashedName);
                }
              }

              if (this.__attached || !observed || val === false) {
                // emit props$ event after each set
                this.__innerProps$!.getValue()[prop.name] = val;
                this.__innerProps$!.next(this.__innerProps$!.getValue());
              }
            },
          });
        });
      },
    },
  };
}

export function setPropFromAttr(
  customElement: DSCustomElement,
  attrName: string,
  attrValue: any,
  mFnHashByName: Maybe<Dict<any>>,
  propInitializer?: Dict<any>
): void {
  const camelName = toCamelCase(attrName);

  customElement[camelName] = mFnHashByName
    .map(fnHashByName => {
      const propConfig = fnHashByName.get(camelName);
      let propValue = attrValue;

      if (propConfig && propConfig.reflectAs && attrValue) {
        propValue = propConfig.reflectAs(attrValue);
      }
      if (propConfig && propConfig.reflectAs === Boolean) {
        // boolean attributes use empty string as true
        propValue = attrValue === '';
      }
      
      if (propInitializer) {
          propInitializer[camelName] = propValue;
      }

      return propValue;
    })
    .withDefault(attrValue);
}
